import { FASTElement, customElement, observable, Updates } from '@microsoft/fast-element';
import { SiteColors, addMediaListeners, darkModeQuery, mobileQuery, removeMediaListeners, setColorScheme } from '../style.js';

import template from './template.js';
import styles from './styles.js';
import { neutralBaseColor } from '@adaptive-web/adaptive-ui/reference';

import customerData from '../customer-data.json';

@customElement({
	name: 'site-root',
	template,
	styles
})
export class Root extends FASTElement {
	private intersectionObserver!: IntersectionObserver;

	@observable
	public header!: HTMLElement;
	private headerChanged(_oldValue: HTMLElement, newValue: HTMLElement): void {
		if (newValue) {
			Updates.enqueue(() => {
				const rect = newValue.getBoundingClientRect();
				this.headerHeight = rect.height;
			});
		}
	}

	@observable
	public headerHeight: number = 0;

	@observable
	public mainContentArea!: HTMLElement;

	@observable
	public contentIsIntersecting: boolean = false;

	@observable
	public isMobileScreen: boolean = false;

	@observable
	public headerCTA!: FASTElement;

	@observable
	public heroCTA!: FASTElement;

	@observable
	public contact!: HTMLElement;

	@observable
	public customerData = customerData;

	public handleClick(): void | boolean {
		this.contact.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}

	@observable
	public colorScheme: 'light' | 'dark' = 'light';

	private handleMobileScreen(event: MediaQueryListEvent | MediaQueryList) {
		if (event.matches) {
			this.isMobileScreen = true;
		} else {
			this.isMobileScreen = false;
		}
	}

	private handleDarkMode(event: MediaQueryListEvent): void {
		if (event.matches) {
			this.colorScheme = 'dark';
		} else {
			this.colorScheme = 'light';
		}
	}

	public async connectedCallback(): Promise<void> {
		super.connectedCallback();

		setColorScheme(darkModeQuery);
		addMediaListeners();
		this.handleMobileScreen(mobileQuery);
		mobileQuery.addEventListener('change', this.handleMobileScreen.bind(this));
		darkModeQuery.addEventListener('change', this.handleDarkMode.bind(this));

		if (darkModeQuery.matches) {
			this.colorScheme = 'dark';
		}

		this.intersectionObserver = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				this.contentIsIntersecting = entry.isIntersecting;
			})
		}, {
			threshold: 0,
			rootMargin: '-1% 0% -99% 0%'
		});

		this.intersectionObserver.observe(this.mainContentArea);

		await Updates.next();
		neutralBaseColor.setValueFor(this.headerCTA, SiteColors.brandBlue);
		neutralBaseColor.setValueFor(this.heroCTA, SiteColors.brandBlue);

		if (window.location.href.includes('#get-connected')) {
			this.contact.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}

	public disconnectedCallback(): void {
		removeMediaListeners();
		mobileQuery.removeEventListener('change', this.handleMobileScreen.bind(this));
		darkModeQuery.removeEventListener('change', this.handleDarkMode.bind(this));

		super.disconnectedCallback();		
	}
}